// @flow @format
import Api from 'lib/blocks-api';
import Store, {
	DOWNLOAD_RUNNING_STATUS,
	DOWNLOAD_RETRY_STATUS,
	DOWNLOAD_PAUSED_STATUS,
	DOWNLOAD_PAUSING_STATUS,
	DOWNLOAD_COMPLETED_STATUS,
	DOWNLOAD_FAILED_STATUS,
} from './store';
import {Downloader, FileDownloader, FolderDownloader} from './downloader';
import * as fs from 'lib/fs';

import type {Repository, Metafile} from 'lib/cloner-api';
import type {DownloadRecord, DownloadRecordStatus} from './store';

// DownloadSummary is a short version of a DownloadRecord.
export type DownloadSummary = {
	id: string,
	hash: string,
	owner: string,
	offset: number,
	machineId: string,
	repositoryId: string,
	diskSize: number,
	filename: string,
	isFile: boolean,
	initialized: boolean,
	size: number,
	downloaded: number,
	createdAt: Date,
	updatedAt: Date,
	status: DownloadRecordStatus,
};

export function downloadSummary(record: DownloadRecord): DownloadSummary {
	return {
		id: record.id,
		hash: record.hash,
		size: record.size,
		owner: record.owner,
		offset: record.offset,
		filename: record.filename,
		diskSize: record.diskSize,
		isFile: record.isFile,
		machineId: record.machineId,
		initialized: record.initialized,
		repositoryId: record.repositoryId,
		downloaded: record.downloaded,
		createdAt: record.createdAt,
		updatedAt: record.updatedAt,
		status: record.status,
	};
}

interface Subscription {
	onSuccess: (fileRecord: DownloadSummary) => void;
	onChange: (fileRecord: DownloadSummary) => void;
}

interface ThroughputSubscription {
	onUpdate: (throughput: number) => any;
}

class IndexedList<K, V> {
	map: Map<K, V>;
	queue: V[];

	constructor() {
		this.map = new Map();
		this.queue = [];
	}

	get(k: K): ?V {
		return this.map.get(k);
	}

	has(k: K): boolean {
		return this.map.has(k);
	}

	top(): V {
		return this.queue[0];
	}

	empty(): boolean {
		return this.queue.length === 0;
	}

	toFront(k: K): void {
		const v = this.map.get(k);

		if (v) {
			const index = this.queue.indexOf(v);
			if (index !== -1) {
				this.queue.splice(index, 1);
			}
			this.queue.unshift(v);
		}
	}

	delete(k: K): ?V {
		const v = this.map.get(k);
		this.map.delete(k);

		if (v) {
			const index = this.queue.indexOf(v);
			if (index !== -1) {
				this.queue.splice(index, 1);
			}
		}

		return v;
	}

	push(k: K, v: V): number {
		this.map.set(k, v);
		return this.queue.push(v);
	}
}

const statusesValues = {
	pausing: 0,
	paused: 1,
	setup: 2,
	running: 3,
	completed: 4,
	failed: 5,
	retry: 6,
};

class DownloadManager {
	static requiredQuota = 1024 * 1024 * 1024 * 1024 * 1024;

	_api: Api;
	_database: Store;
	_dirEntry: FileSystemDirectoryEntry;
	_subscriptions: Set<Subscription>;
	_queue: IndexedList<string, Downloader>;

	_downloading: boolean;

	// Throughput calculation variables.
	_throughputSubscriptions: Set<ThroughputSubscription>;
	_downloaded: number;
	_downloadedSamples: number[];
	_downloadSampleIndex: number;
	_throughputInterval: number;

	constructor(
		key: string,
		token: string,
		server: string,
		dirEntry: FileSystemDirectoryEntry
	) {
		this._api = new Api(server, token);
		this._database = new Store(key);
		this._dirEntry = dirEntry;
		this._subscriptions = new Set();
		this._queue = new IndexedList();

		this._downloading = false;

		this._throughputSubscriptions = new Set();
		this._downloaded = 0;
		this._downloadedSamples = Array.from({length: 10}, () => 0);
		this._downloadSampleIndex = 0;
		this._throughputInterval = 0;
	}

	subscribe(subscription: Subscription) {
		this._subscriptions.add(subscription);
	}

	unsubscribe(subscription: Subscription) {
		this._subscriptions.delete(subscription);
	}

	subscribeTroughput(subscription: ThroughputSubscription) {
		this._throughputSubscriptions.add(subscription);

		if (this._throughputInterval === 0) {
			this._throughputInterval = setInterval(
				this._notifyTroughput.bind(this),
				1000
			);
		}
	}

	unsubscribeTroughput(subscription: ThroughputSubscription) {
		this._throughputSubscriptions.delete(subscription);

		if (this._throughputSubscriptions.size === 0) {
			clearInterval(this._throughputInterval);
			this._throughputInterval = 0;
		}
	}

	allDownloads(
		...statuses: DownloadRecordStatus[]
	): Promise<DownloadRecord[]> {
		return this._database.allDownloads(...statuses).then(downloads => {
			if (downloads.length > 0) {
				return downloads;
			}

			return this._database.allDownloads(
				...statuses.map(status => statusesValues[status])
			);
		});
	}

	// removeDownload removes the given download and returns the released size in bytes.
	async removeDownload(id: string) {
		// check if the download is already in the queue
		let downloader: Downloader;

		let deleted = this._queue.delete(id);
		if (!deleted) {
			const download = await this._database.getDownload(id);

			if (download.isFile) {
				downloader = new FileDownloader(
					download,
					this._dirEntry,
					this._database,
					this._api
				);
			} else {
				downloader = new FolderDownloader(
					download,
					this._dirEntry,
					this._database,
					this._api
				);
			}
		} else {
			downloader = deleted;
		}

		if (
			downloader.download.status === DOWNLOAD_RUNNING_STATUS ||
			downloader.download.status === DOWNLOAD_RETRY_STATUS ||
			downloader.download.status === DOWNLOAD_PAUSING_STATUS
		) {
			// wait until download is stopped
			await new Promise((resolve, reject) => {
				const {onChange, onSuccess} = downloader;

				downloader.onSuccess = (download: DownloadRecord) => {
					onSuccess && onSuccess(download);
					downloader.onSuccess = onSuccess;
					downloader.onChange = onChange;
					resolve();
				};

				downloader.onChange = (download: DownloadRecord) => {
					onChange && onChange(download);

					if (
						download.id === id &&
						(download.status === DOWNLOAD_PAUSED_STATUS ||
							download.status === DOWNLOAD_FAILED_STATUS)
					) {
						downloader.onSuccess = onSuccess;
						downloader.onChange = onChange;
						resolve();
					}
				};

				downloader && downloader.stop();
			});
		}

		// we can safely remove the download at this point.
		await downloader.remove();
	}

	async saveDownloadedFile(id: string) {
		const download = await this._database.getDownload(id);
		const fileEntry = await fs.getFileEntry(this._dirEntry, download.id);
		const a = document.createElement('a');

		a.href = fileEntry.toURL();

		if (download.isFile) {
			a.download = download.filename;
		} else {
			a.download = download.filename + '.zip';
		}

		a.click();
	}

	async pause(id: string) {
		const downloader = this._queue.get(id);

		if (downloader) {
			this._downloading = false;
			await downloader.stop();
		}
	}

	_notifyTroughput() {
		this._downloadedSamples[this._downloadSampleIndex] = this._downloaded;

		const lastIndex = this._downloadSampleIndex;

		this._downloadSampleIndex =
			(lastIndex + 1) % this._downloadedSamples.length;

		let throughput =
			(this._downloadedSamples[lastIndex] -
				this._downloadedSamples[this._downloadSampleIndex]) /
			this._downloadedSamples.length;

		if (this._downloadedSamples[this._downloadSampleIndex] === 0) {
			throughput = this._downloadedSamples[lastIndex] / (lastIndex + 1);
		}

		for (const subscription of this._throughputSubscriptions) {
			subscription.onUpdate(throughput * 8);
		}
	}

	_listen(downloader: Downloader) {
		downloader.onChange = (download: DownloadRecord) => {
			if (
				download.status === DOWNLOAD_COMPLETED_STATUS ||
				download.status === DOWNLOAD_FAILED_STATUS
			) {
				this._queue.delete(download.id);

				if (this._queue.empty() && this._downloading) {
					this._downloading = false;
				}

				if (this._downloading) {
					const next = this._queue.top();
					if (next.status === 'stopped') {
						next.start();

						this._downloaded = 0;
						this._downloadedSamples = Array.from(
							{length: 10},
							() => 0
						);
					}
				}
			}

			this._downloaded = download.downloaded;

			const summary = downloadSummary(download);
			for (const subscription of this._subscriptions) {
				subscription.onChange(summary);
			}
		};

		downloader.onSuccess = (download: DownloadRecord) => {
			const summary = downloadSummary(download);
			for (const subscription of this._subscriptions) {
				subscription.onSuccess(summary);
			}
		};
	}

	async addDownload(
		repository: Repository,
		metafile: Metafile,
		includeDeleted: boolean = false
	): Promise<DownloadSummary> {
		let download = await this._database.getDownload(metafile.Id);

		if (!download) {
			download = await this._database.createDownload(
				repository,
				metafile,
				includeDeleted
			);
		}

		return downloadSummary(download);
	}

	async append(id: string) {
		let download = await this._database.getDownload(id);

		if (!download) {
			return;
		}

		let downloader: Downloader;

		if (download.isFile) {
			downloader = new FileDownloader(
				download,
				this._dirEntry,
				this._database,
				this._api
			);
		} else {
			downloader = new FolderDownloader(
				download,
				this._dirEntry,
				this._database,
				this._api
			);
		}

		this._listen(downloader);
		this._queue.push(download.id, downloader);

		await downloader.init();
	}

	isRunning(): boolean {
		return this._downloading;
	}

	top() {
		const downloader = this._queue.top();
		if (downloader) {
			return downloader.download;
		}
		return null;
	}

	async start(id: string) {
		// check if the download is already in the queue.
		// if (!this._queue.has(id)) {
		const next = this._queue.get(id);

		if (!next) {
			console.warn(
				`Download ${id} is not registered in download manager`
			);
			return;
		}

		const top = this._queue.top();

		if (top.download.id !== id) {
			// wait until top is stopped.
			await new Promise((resolve, reject) => {
				if (top.download.isFile || next.download.isFile) {
					top.stop();
				} else {
					top.cancel();
				}

				if (
					top.download.status === DOWNLOAD_RUNNING_STATUS ||
					top.download.status === DOWNLOAD_PAUSING_STATUS ||
					top.download.status === DOWNLOAD_RETRY_STATUS
				) {
					const {onSuccess, onChange} = top;

					top.onSuccess = (download: DownloadRecord) => {
						onSuccess && onSuccess(download);

						top.onSuccess = onSuccess;
						top.onChange = onChange;

						resolve();
					};

					top.onChange = (download: DownloadRecord) => {
						onChange && onChange(download);

						if (
							download.status === DOWNLOAD_PAUSED_STATUS ||
							download.status === DOWNLOAD_FAILED_STATUS
						) {
							top.onSuccess = onSuccess;
							top.onChange = onChange;

							resolve();
						}
					};
					return;
				}

				resolve();
			});
		}

		this._queue.toFront(id);
		this._queue.top().start();

		this._downloading = true;

		this._downloaded = 0;
		this._downloadedSamples = Array.from({length: 10}, () => 0);

		return;
	}
}

export default DownloadManager;



// WEBPACK FOOTER //
// ./src/lib/downloader/manager.js